import { css } from '@emotion/react'
import { useContext, useEffect, useMemo } from 'react'
import { colors, fonts, timings, getTrainingGoalZoneSelection } from 'shared-ui'
import { PitchBg } from 'shared-ui'
import useRawState from '../../hooks/useRawState';
import { useState } from 'react';
import Slider from 'rc-slider'
import 'rc-slider/assets/index.css'
import { useIntl } from 'react-intl';
import Orientation from '../test/Orientation';
import ScoreIndicator from '../_general/ScoreIndicator';
import PitchIndexPdfContext from '../../context/PitchIndexPdfData';

//import PitchTelemetry from '../../context/covermaster/PitchTelemetry'
// TO DO: check covermaster implementations of ZoneResults

/*
    X and Y are NOT the on-screen positions
    The results are shown on-screen as follows

      Y---->
      /====================\
      |____________________|
    ^ |____________________|
    | |____________________|
    | |____________________|
    X \====================/
*/

/* zoneresults: [
    {measurement, posX, posY, weight, zoneType}
] */

export default function TrainingGoalZones({
    backgroundType,
    zoneResults = [],
    templateIRI,
    orientation,
    showInfoBar = true,
    ...props
}) {

    // fetch detailed info about the zones template (to find the goalmouths)
    const { data: template } = useRawState(templateIRI)

    const [sliderPercentage, setSliderPercentage] = useState(50)

    const { setTrainingGoalPercentage } = useContext(PitchIndexPdfContext)

    // keep the PDF context in sync with wherever the slider is currently dragged to
    useEffect(() => {
        setTrainingGoalPercentage(sliderPercentage)
    }, [sliderPercentage])

    // shared with the pdf-service's TrainingGoalZonesViewer so the on-screen page and
    // exported PDF always agree on which zones are highlighted for a given percentage
    const { dimX, dimY, zoneSize, isPercentageTemplate, zonesSelection, selectedAvg } = useMemo(() => (
        getTrainingGoalZoneSelection(zoneResults, template, sliderPercentage)
    ), [zoneResults, template, sliderPercentage])

    const { formatMessage } = useIntl()

    return (
        <div
            css={css`
                display: flex; 
                flex-direction: column; 
                align-items: stretch; 
                justify-content: center;
                gap: 0.2em;
            `}
            {...props}
        >
            <div css={{ position: 'relative', display: 'flex' }}>
                <PitchBg
                    type={backgroundType}
                    color={'#fff'}
                    css={{ flexGrow: 1 }}
                />

                <div css={{
                    display: 'grid',
                    width: '100%',
                    height: '100%',
                    gridTemplateRows: isPercentageTemplate ? 'none' : `repeat(${dimX}, 1fr)`,
                    gridTemplateColumns: isPercentageTemplate ? 'none' : `repeat(${dimY}, 1fr)`,
                    position: 'absolute',
                    placeItems: 'stretch',
                }}>
                    {zonesSelection.map((zone) => {

                        const isBad = zone.measurement < 5
                        const zoneColor = isBad ? colors.soft : colors.main2

                        return <div
                            css={[
                                isPercentageTemplate ?
                                    css`
                                        height: ${zoneSize}%;
                                        aspect-ratio: 1;
                                        border-radius: 50%;
                                        display: grid;
                                        place-items: center;
                                        position: absolute;
                                        overflow: visible;

                                        top: ${100 - zone.posX}%;
                                        left: ${zone.posY}%;
                                        transform: translate(-50%, -50%);

                                        .orb {
                                            position: absolute;
                                            height: 100%;
                                            aspect-ratio: 1;
                                            max-height: 3.5rem;
                                            filter: drop-shadow(0 0 0.5em ${zoneColor});
                                            fill: ${zoneColor};
                                            opacity: ${isBad ? 0.8 : 1};
                                        }
                                    `
                                    :
                                    css`
                                        height: 100%;
                                        width: 100%;
                                        display: ${(zone.weight === undefined || !!zone.weight) ? 'grid' : 'none'};  // hides zone when weight is 0 or null
                                        place-items: center;
                                        overflow: visible;
                                        grid-column: ${zone.posY};
                                        // add one because grid-row should range from 1 to dimX
                                        // and posX ranges from 1 to dimX
                                        grid-row: ${dimX - zone.posX + 1};
                                        background: ${zoneColor};
                                        opacity: ${isBad ? 0.25 : 0.5};
                                    `
                                ,
                            ]}
                            key={`zoneResult-index-(${zone.posX},${zone.posY})`}
                        >
                            {isPercentageTemplate &&
                                <svg className='orb' viewBox='0 0 100 100' preserveAspectRatio='xMidYMid meet'>
                                    <circle cx='50' cy='50' r='50' />
                                </svg>
                            }
                        </div>
                    })}
                </div>
            </div>

            {showInfoBar &&
                <div css={style.sliderWrapper}>
                    <ScoreIndicator
                        isNull={selectedAvg == undefined}
                        value={selectedAvg}
                        decimals={1}
                        color={selectedAvg < 5 ? colors.soft : colors.main2}
                        textColor={'white'}
                        css={style.ScoreIndicator}
                    />

                    <Slider
                        value={sliderPercentage}
                        step={1}
                        min={1}
                        max={100}
                        onChange={(val) => setSliderPercentage(val)}
                        css={style.slider}
                    />
                    <div className='info' css={style.info}>
                        <div css={css`display: flex; gap: 0.5em;`}>
                            {formatMessage({ id: "topXPercentOfData" }, { percentage: sliderPercentage })}
                        </div>
                    </div>
                    <Orientation heading={orientation} color={colors.main2} css={css`background: transparent; box-shadow: unset;`} />
                </div>
            }
        </div>
    )
}

const style = {
    sliderWrapper: css`
        width: 100%;
        background: rgba(255,255,255,0.07);
        height: 2.5em;
        border-radius: 5px;
        display: flex;
        align-items: center;
        padding: 0 0.5em 0 1em;
        gap: 1em;
        color: ${colors.soft};
        position: relative;

        &:hover {
            .info {
                visibility: unset;
                opacity: 1;
                transform: translate(-50%, -130%);
            }
        }
    `,
    info: css`
        position: absolute;
        top: 0;
        left: 50%;
        transform: translate(-50%,0);
        display: grid;
        place-items: center;
        color: white;
        visibility: hidden;
        opacity: 0;
        transition: opacity, transform ${timings.smooth};
        border-radius: 5px;
    `,
    slider: css`
        cursor: pointer !important;

        .rc-slider-rail, .rc-slider-track {
            background: ${colors.light};
        }

        .rc-slider-handle {
            border-color: ${colors.eventLight} !important;
            background: ${colors.eventLight} !important;
            height: 1.5em;
            width: 1.5em;
            border-radius: 0;
            transform: translate(-50%, -20%) !important;
            opacity: 1 !important;
            border-radius: 1em;
            border: 2px solid ${colors.light} !important;
            box-shadow: unset !important; 
        }

        .rc-slider-handle-dragging {
            border-color: ${colors.light} !important;
            background: ${colors.light} !important;
            box-shadow: unset !important; 
        }
    `,
    ScoreIndicator: css`
        font-size: 0.66em;
        margin-right: 0.5em;
        font-family: ${fonts.special};
    `,
}